import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../utils/seo';
import './LandingPage.css';
import DemoVideoModal from '../components/DemoVideoModal';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  return (
    <motion.div
      ref={ref} className={className}
      initial="hidden" animate={inView ? 'visible' : 'hidden'}
      variants={fadeUp}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay }}
    >{children}</motion.div>
  );
}

const POPULAR_AGENTS = [
  {
    id: 'blog-post',
    emoji: '✍️',
    name: 'Artículo de Blog',
    desc: 'Posts SEO completos desde un briefing. Título, subtítulos, cuerpo — listo para publicar.',
    category: 'Contenido',
    plan: 'free',
    color: '#6366f1',
    route: '/category/content',
  },
  {
    id: 'market-analysis',
    emoji: '📊',
    name: 'Análisis de Mercado',
    desc: 'Panorama del sector, competencia y oportunidades en formato estructurado.',
    category: 'Estrategia',
    plan: 'grow',
    color: '#0891b2',
    route: '/category/strategy',
  },
  {
    id: 'google-ads',
    emoji: '🎯',
    name: 'Creador de Google Ads',
    desc: 'Titulares, descripciones y grupos de anuncios listos para campaña desde tu briefing.',
    category: 'Digital',
    plan: 'grow',
    color: '#059669',
    route: '/category/digital',
  },
  {
    id: 'brand-identity',
    emoji: '🎨',
    name: 'Guía de Identidad de Marca',
    desc: 'Voz, paleta, tipografía y normas de uso para cualquier marca — en minutos.',
    category: 'Creativo',
    plan: 'scale',
    color: '#be185d',
    route: '/category/creative',
  },
  {
    id: 'investor-pitch',
    emoji: '🚀',
    name: 'Pitch para Inversores',
    desc: 'Narrativa diapositiva a diapositiva, financieros y posicionamiento inversor.',
    category: 'Startup',
    plan: 'evolution',
    color: '#ea580c',
    route: '/category/startup',
  },
  {
    id: 'financial-forecast',
    emoji: '💰',
    name: 'Previsión Financiera',
    desc: 'P&L a 12 meses, proyecciones de ingresos y análisis de escenarios.',
    category: 'Finanzas',
    plan: 'evolution',
    color: '#16a34a',
    route: '/category/finance',
  },
];

const PLAN_COLORS = {
  free: { bg: 'rgba(99,102,241,0.12)', text: '#a5b4fc' },
  grow: { bg: 'rgba(16,185,129,0.12)', text: '#34d399' },
  scale: { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24' },
  evolution: { bg: 'rgba(239,68,68,0.1)', text: '#f87171' },
  addon: { bg: 'rgba(6,182,212,0.12)', text: '#22d3ee' },
};
const PLAN_LABELS = { free: 'Free', grow: 'Grow', scale: 'Scale', evolution: 'Evolution', addon: 'Add-on' };

/* ── Aran Demo Panel ──────────────────────────────────────────────────────── */
const ARD_SCENARIOS = [
  {
    brand: 'ShopDutyFree', color: '#D4890A', init: 'SD',
    channels: [{ label: 'LinkedIn', on: true }, { label: 'Gmail', on: true }, { label: 'Instagram', on: true }],
    signal: { cls: 'li', icon: '💼', src: 'LinkedIn · hace 3 min', txt: 'Competidor publica sobre IA en retail — 2.4K likes · 847 reposts en 1h', time: 'hace 3 min' },
    steps: ['Señal detectada · LinkedIn', 'Analizando contexto de marca', 'Generando contenido', 'Listo para publicar'],
    msgs: [
      { txt: 'Señal de alto engagement en LinkedIn. Tu competidor activa conversación sobre IA en retail.', hi: false },
      { txt: 'Oportunidad antes de que el tema se agote. Genero post optimizado para ShopDutyFree:', hi: true },
    ],
    ctTag: 'Instagram · Carrusel', ctStat: '<b>+340%</b> engagement estimado',
    content: `✨ La IA no reemplaza la experiencia de compra — la amplifica.\n\nEn ShopDutyFree, cada recomendación nace de tus preferencias reales.\n\nNo es tecnología. Es atención al detalle a escala.\n\n→ Descubre cómo en el link de bio.\n\n#RetailInnovation #ExperienciaDeCompra`,
  },
  {
    brand: 'RiojaLta Wines', color: '#8B1F35', init: 'RL',
    channels: [{ label: 'Gmail', on: true }, { label: 'Instagram', on: true }, { label: 'LinkedIn', on: false }],
    signal: { cls: 'gm', icon: '✉️', src: 'Gmail · hace 15 min', txt: '3 distribuidores abrieron tu propuesta — sin respuesta en 4 días', time: 'hace 15 min' },
    steps: ['Señal detectada · Gmail', 'Identificando leads calientes', 'Redactando seguimiento', 'Listo para enviar'],
    msgs: [
      { txt: '3 leads han abierto tu propuesta pero no respondieron en 4 días. Timing crítico.', hi: false },
      { txt: 'El día 4 tiene mayor tasa de reactivación. Redacto seguimiento que mueve sin presionar:', hi: true },
    ],
    ctTag: 'Email · Seguimiento', ctStat: '<b>+68%</b> tasa respuesta estimada',
    content: `Hola [nombre],\n\nVi que revisaste nuestra propuesta esta semana.\n\nSé que estáis en plena selección para primavera. Por eso quiero hacerte fácil el siguiente paso:\n\n→ 15 min para resolver cualquier duda\n→ Muestras de las 3 nuevas referencias sin compromiso\n\n¿Te viene bien el martes o el jueves?`,
  },
  {
    brand: 'Clínica Avance', color: '#1A6FBF', init: 'CA',
    channels: [{ label: 'Instagram', on: true }, { label: 'Gmail', on: true }, { label: 'WhatsApp', on: true }],
    signal: { cls: 'ig', icon: '📸', src: 'Instagram · hace 1 h', txt: 'Tu Reel del martes tiene 3× más saves de lo normal — algoritmo favorece el formato', time: 'hace 1 h' },
    steps: ['Señal detectada · Instagram', 'Analizando qué funcionó', 'Creando variante ganadora', 'Listo para publicar'],
    msgs: [
      { txt: 'Tu Reel del martes está funcionando excepcionalmente bien. 3× más saves que tu media.', hi: false },
      { txt: 'El hook y el formato son el motor. Creo variante para capitalizar el momentum:', hi: true },
    ],
    ctTag: 'Reel · Variante', ctStat: 'Alcance estimado <b>14.2K</b>',
    content: `👁️ Lo que nadie te dice sobre la recuperación.\n\n[REEL]\nHook (0–3s): «¿Cuándo puedo volver al gym?» — la pregunta que más nos hacen.\nBloque 1: La respuesta que da todo el mundo vs. la respuesta real.\nBloque 2: Los 3 factores que lo cambian todo.\nCTA: Agenda tu consulta — link en bio.\n\n#CirugíaPlástica #Recuperación #ConsultaGratis`,
  },
];

function AranDemoPanel() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const get = id => root.querySelector(`[data-ard="${id}"]`);

    let timers = [];
    let twTimer = null;
    let idx = 0;

    function clear() {
      timers.forEach(clearTimeout);
      timers = [];
      if (twTimer) { clearInterval(twTimer); twTimer = null; }
    }
    function after(fn, ms) { timers.push(setTimeout(fn, ms)); }

    function typewrite(el, text, speed, onDone) {
      el.innerHTML = '';
      const cursor = document.createElement('span');
      cursor.className = 'ard-cursor';
      el.appendChild(cursor);
      let i = 0;
      twTimer = setInterval(() => {
        if (i < text.length) {
          cursor.insertAdjacentText('beforebegin', text[i++]);
          el.scrollTop = el.scrollHeight; // fixed-height box: keep the cursor in view
        }
        else { clearInterval(twTimer); twTimer = null; cursor.remove(); onDone && onDone(); }
      }, speed);
    }

    function addMsg(msgs, s, mi) {
      const m = s.msgs[mi];
      const wrap = document.createElement('div');
      wrap.className = 'ard-msg';
      wrap.innerHTML = `<div class="ard-av ard-av-sm">A</div><div class="ard-bubble${m.hi ? ' hi' : ''}">${m.txt}</div>`;
      const typing = get('typing');
      msgs.insertBefore(wrap, typing);
      requestAnimationFrame(() => wrap.classList.add('show'));
    }

    function setStep(stepEls, i) {
      stepEls.forEach((el, j) => {
        el.classList.remove('active', 'done');
        if (j < i) el.classList.add('done');
        else if (j === i) el.classList.add('active');
      });
    }

    function updateDots() {
      root.querySelectorAll('.ard-d').forEach((d, i) => d.classList.toggle('on', i === idx));
    }

    function run(s) {
      get('brandAv').style.background = s.color;
      get('brandAv').textContent = s.init;
      get('brandNm').textContent = s.brand;
      get('chips').innerHTML = s.channels.map(c =>
        `<div class="ard-chip${c.on ? ' on' : ''}"><span class="ard-chip-dot"></span>${c.label}</div>`
      ).join('');
      const sigCard = get('sigCard');
      sigCard.classList.remove('show');
      const sigIco = get('sigIco');
      sigIco.className = `ard-sig-ico ${s.signal.cls}`;
      sigIco.textContent = s.signal.icon;
      get('sigSrc').textContent = s.signal.src;
      get('sigTxt').textContent = s.signal.txt;
      get('sigTime').textContent = s.signal.time;
      get('ctTag').textContent = s.ctTag;
      get('ctStat').innerHTML = s.ctStat;
      get('ctText').innerHTML = '';
      get('ctActions').classList.remove('show');

      const stepsEl = get('steps');
      stepsEl.innerHTML = s.steps.map(t =>
        `<div class="ard-step"><span class="ard-step-dot"></span>${t}</div>`
      ).join('');
      const stepEls = Array.from(stepsEl.querySelectorAll('.ard-step'));

      const msgs = get('msgs');
      msgs.querySelectorAll('.ard-msg').forEach(el => el.remove());
      const typing = get('typing');
      typing.classList.remove('show');

      after(() => { sigCard.classList.add('show'); }, 900);
      after(() => { setStep(stepEls, 0); }, 1800);
      after(() => { typing.classList.add('show'); }, 2400);
      after(() => { addMsg(msgs, s, 0); setStep(stepEls, 1); }, 3600);
      after(() => { addMsg(msgs, s, 1); setStep(stepEls, 2); typing.classList.remove('show'); }, 5200);
      after(() => {
        typewrite(get('ctText'), s.content, 18, () => {
          get('ctActions').classList.add('show');
          setStep(stepEls, 3);
        });
      }, 6400);
      after(() => {
        sigCard.classList.remove('show');
        setTimeout(() => { idx = (idx + 1) % ARD_SCENARIOS.length; updateDots(); run(ARD_SCENARIOS[idx]); }, 450);
      }, 14000);
    }

    const dotsEl = get('dots');
    ARD_SCENARIOS.forEach((_, i) => {
      const d = document.createElement('button');
      d.className = i === 0 ? 'ard-d on' : 'ard-d';
      d.setAttribute('aria-label', `Escenario ${i + 1}`);
      d.addEventListener('click', () => {
        if (i === idx) return;
        clear();
        get('sigCard').classList.remove('show');
        idx = i; updateDots(); run(ARD_SCENARIOS[idx]);
      });
      dotsEl.appendChild(d);
    });

    run(ARD_SCENARIOS[0]);
    return () => { clear(); dotsEl.innerHTML = ''; };
  }, []);

  return (
    <div className="ard-wrap" ref={rootRef}>
      <div className="ard-panel">
        {/* Left — Aran chat */}
        <div className="ard-chat">
          <div className="ard-chat-hd">
            <div className="ard-av">A</div>
            <div className="ard-chat-hd-info">
              <div className="ard-chat-name">Aran</div>
              <div className="ard-live">En vivo</div>
            </div>
          </div>
          <div className="ard-steps" data-ard="steps"></div>
          <div className="ard-msgs" data-ard="msgs">
            <div className="ard-typing" data-ard="typing">
              <div className="ard-av ard-av-sm">A</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <div className="ard-dot"></div>
                <div className="ard-dot"></div>
                <div className="ard-dot"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Right — Workspace */}
        <div className="ard-ws">
          <div className="ard-ws-hd">
            <div className="ard-brand-row">
              <div className="ard-brand-av" data-ard="brandAv"></div>
              <div>
                <div className="ard-brand-nm" data-ard="brandNm">—</div>
                <div className="ard-brand-ws">Workspace</div>
              </div>
            </div>
            <div className="ard-chips" data-ard="chips"></div>
          </div>
          <div className="ard-ws-sig">
            <div className="ard-lbl">Señales</div>
            <div className="ard-sig-card" data-ard="sigCard">
              <div className="ard-sig-ico" data-ard="sigIco"></div>
              <div className="ard-sig-body">
                <div className="ard-sig-new">Nueva señal</div>
                <div className="ard-sig-src" data-ard="sigSrc"></div>
                <div className="ard-sig-txt" data-ard="sigTxt"></div>
              </div>
              <div className="ard-sig-time" data-ard="sigTime"></div>
            </div>
          </div>
          <div className="ard-ws-content">
            <div className="ard-lbl">Generado por Aran</div>
            <div className="ard-content-box">
              <div className="ard-content-top">
                <div className="ard-ct-tag" data-ard="ctTag">—</div>
                <div className="ard-ct-stat" data-ard="ctStat"></div>
              </div>
              <div className="ard-ct-text" data-ard="ctText"></div>
              <div className="ard-ct-actions" data-ard="ctActions">
                <button className="ard-btn ard-btn-p">Publicar ahora</button>
                <button className="ard-btn ard-btn-s">Ajustar</button>
                <button className="ard-btn ard-btn-s">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ard-dots" data-ard="dots"></div>
    </div>
  );
}

const TOOL_CATEGORIES = [
  { id: 'marketing', emoji: '📈', name: 'Marketing & Crecimiento', plan: 'free', color: '#7c3aed', tools: ['Investigación de Keywords', 'Generador de Titulares', 'Copies para Redes', 'Campaña de Email', 'Nota de Prensa'], count: 6 },
  { id: 'content', emoji: '✍️', name: 'Creación de Contenido', plan: 'free', color: '#6366f1', tools: ['Artículo de Blog', 'Guión de Vídeo', 'Newsletter', 'Generador de Logo'], count: 5 },
  { id: 'strategy', emoji: '🎯', name: 'Estrategia de Negocio', plan: 'grow', color: '#0891b2', tools: ['Plan de Negocio', 'Análisis de Mercado', 'Investigación de Competencia', 'Análisis DAFO'], count: 6 },
  { id: 'digital', emoji: '🛠️', name: 'Marketing Digital', plan: 'grow', color: '#059669', tools: ['Creador de Google Ads', 'Anuncios en Meta', 'Copy para Landing'], count: 3 },
  { id: 'ecommerce', emoji: '🛒', name: 'Crecimiento E-commerce', plan: 'scale', color: '#d97706', tools: ['Ficha Amazon', 'Descripción de Producto', 'Optimizador CRO'], count: 3 },
  { id: 'agency', emoji: '🏢', name: 'Herramientas de Agencia', plan: 'scale', color: '#7c3aed', tools: ['Propuesta de Cliente', 'Informe de Cliente', 'Caso de Éxito'], count: 3 },
  { id: 'creative', emoji: '🎨', name: 'Estudio Creativo', plan: 'scale', color: '#be185d', tools: ['Guía de Identidad de Marca', 'Estudio de Imágenes IA', 'Plan de Producción de Vídeo', 'Prompts para Kling'], count: 5 },
  { id: 'startup', emoji: '🚀', name: 'Lanzamiento de Startup', plan: 'evolution', color: '#ea580c', tools: ['Pitch para Inversores', 'Estrategia Go-to-Market', 'Generador de User Stories'], count: 3 },
  { id: 'finance', emoji: '💰', name: 'Finanzas e Inversión', plan: 'evolution', color: '#16a34a', tools: ['Previsión Financiera', 'Análisis de Inversión', 'Optimizador de Flujo de Caja'], count: 3 },
  { id: 'automation', emoji: '⚡', name: 'Automatización n8n', plan: 'addon', color: '#0891b2', tools: ['Diseñador de Flujos n8n'], count: 1, isAddon: true },
];

const HERO_TABS = [
  {
    id: 'text', icon: '✍️', label: 'Texto y Chat', color: '#6366f1',
    credit: '2 créditos',
    desc: 'Chatea con IA, escribe contenido, analiza mercados, planifica estrategias.',
    preview: () => (
      <div className="htab__preview htab__preview--text">
        <div className="htab__chat-msg htab__chat-msg--user">Planifica el contenido de este mes para una marca de moda sostenible</div>
        <div className="htab__chat-msg htab__chat-msg--ai">
          <div className="htab__ai-label">⚡ Gormaran AI</div>
          <div className="htab__bars">
            {[85,70,90,60,75,45,80].map((w,i) => <div key={i} className="htab__bar" style={{ width: `${w}%`, animationDelay: `${i*0.08}s` }} />)}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'design', icon: '🎨', label: 'Diseño', color: '#7c3aed',
    credit: '10 créditos',
    desc: 'Genera imágenes, fotos de producto y visuales para redes con DALL·E 3.',
    preview: () => (
      <div className="htab__preview htab__preview--design">
        {['#7c3aed22','#6366f122','#be185d22','#0891b222'].map((bg, i) => (
          <div key={i} className="htab__img-card" style={{ background: bg }}>
            <div className="htab__img-inner" />
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'video', icon: '🎬', label: 'Vídeo', color: '#0284c7',
    credit: '25 créditos',
    desc: 'Convierte texto en vídeos cinematográficos con Kling, Higgsfield y más.',
    preview: () => (
      <div className="htab__preview htab__preview--video">
        <div className="htab__video-frame">
          <div className="htab__video-play">▶</div>
          <div className="htab__video-bar" />
          <div className="htab__video-label">Vídeo IA · 16:9</div>
        </div>
      </div>
    ),
  },
  {
    id: 'audio', icon: '🎵', label: 'Audio', color: '#059669',
    credit: '10 créditos',
    desc: 'Texto a voz en 8 idiomas. Música generada por IA desde una descripción.',
    preview: () => (
      <div className="htab__preview htab__preview--audio">
        <div className="htab__waveform">
          {[30,55,80,45,90,35,70,60,85,40,65,75,50,95,30].map((h,i) => (
            <div key={i} className="htab__wave-bar" style={{ height: `${h}%`, animationDelay: `${i*0.06}s` }} />
          ))}
        </div>
        <div className="htab__audio-label">🎙️ ES · Normal · ▶ Play · ⬇ Descargar</div>
      </div>
    ),
  },
  {
    id: 'agents', icon: '⚡', label: 'Agentes IA', color: '#7c3aed',
    credit: '30 créditos',
    desc: 'Construye mini-apps interactivas y flujos visuales de IA — sin código.',
    preview: () => (
      <div className="htab__preview htab__preview--agents">
        <div className="htab__app-bar">
          <span className="htab__app-dot" /><span className="htab__app-dot" /><span className="htab__app-dot" />
          <span className="htab__app-title">Panel KPIs · Vista en tiempo real</span>
        </div>
        <div className="htab__app-content">
          {[['MRR','€24.8k','+12%'],['Churn','2.4%','-0.3%'],['DAU','1.240','+8%']].map(([k,v,d]) => (
            <div key={k} className="htab__kpi"><div className="htab__kpi-label">{k}</div><div className="htab__kpi-val">{v}</div><div className="htab__kpi-delta">{d}</div></div>
          ))}
        </div>
      </div>
    ),
  },
];

function HeroTabsShowcase() {
  const [active, setActive] = useState(0);
  const tab = HERO_TABS[active];
  const PreviewComp = tab.preview;

  return (
    <div className="htab">
      <div className="htab__tabs">
        {HERO_TABS.map((t, i) => (
          <button
            key={t.id}
            className={`htab__tab${active === i ? ' htab__tab--active' : ''}`}
            style={active === i ? { '--tc': t.color } : {}}
            onClick={() => setActive(i)}
          >
            <span className="htab__tab-icon">{t.icon}</span>
            <span className="htab__tab-label">{t.label}</span>
            <span className="htab__credit">{t.credit}</span>
          </button>
        ))}
      </div>

      <motion.div
        key={active}
        className="htab__card"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        style={{ '--tc': tab.color }}
      >
        <div className="htab__card-left">
          <div className="htab__card-icon">{tab.icon}</div>
          <h3 className="htab__card-title">{tab.label}</h3>
          <p className="htab__card-desc">{tab.desc}</p>
          <div className="htab__card-credit">{tab.credit} por generación</div>
        </div>
        <div className="htab__card-right">
          <PreviewComp />
        </div>
      </motion.div>
    </div>
  );
}

/* eslint-disable */
function RotatingText() {
  const rotatingPhrases = useMemo(() => [
    'restaurantes y hoteles',
    'clínicas y especialistas',
    'inmobiliarias',
    'e-commerce y tiendas',
    'asesorías y despachos',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 1024);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  useEffect(() => { setPhraseIndex(0); setDisplayed(''); setIsDeleting(false); }, [rotatingPhrases]);
  useEffect(() => {
    if (isMobile) return;
    const current = rotatingPhrases[phraseIndex];
    if (!isDeleting && displayed === current) {
      const timer = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(timer);
    }
    if (isDeleting && displayed === '') {
      setIsDeleting(false);
      setPhraseIndex((i) => (i + 1) % rotatingPhrases.length);
      return;
    }
    const speed = isDeleting ? 30 : 60;
    const timer = setTimeout(() => {
      setDisplayed(isDeleting ? current.slice(0, displayed.length - 1) : current.slice(0, displayed.length + 1));
    }, speed);
    return () => clearTimeout(timer);
  }, [displayed, isDeleting, phraseIndex, isMobile, rotatingPhrases]);

  if (isMobile) return <span className="landing__hero-rotating">{rotatingPhrases[0]}</span>;
  return <span className="landing__hero-rotating">{displayed}<span className="landing__hero-cursor" /></span>;
}

// ── Trust Bar ────────────────────────────────────────────────────────────────

function PopularAgentsSection() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const handleTryAgent = (route) => {
    if (currentUser) { navigate(route); } else { navigate('/auth?mode=register'); }
  };
  return (
    <section id="agents" className="landing__agents section">
      <div className="container">
        <AnimatedSection>
          <span className="section-pill">Plantillas más usadas</span>
          <h2 className="section-title">
            Empieza con las plantillas{' '}
            <span className="gradient-text">más populares</span>
          </h2>
          <p className="section-subtitle">
            Cada plantilla está diseñada para una tarea específica. Rellena 3 campos — obtén contenido profesional listo para publicar.
          </p>
        </AnimatedSection>

        <motion.div
          className="landing__agent-cards"
          initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: '-20px' }}
          variants={stagger}
        >
          {POPULAR_AGENTS.map((agent) => (
            <motion.div key={agent.id} variants={fadeUp} className="landing__agent-card" style={{ '--agent-color': agent.color }}>
              <div className="landing__agent-card-top">
                <div className="landing__agent-emoji" style={{ background: `${agent.color}18` }}>{agent.emoji}</div>
                <span className="landing__agent-plan-badge" style={{ background: PLAN_COLORS[agent.plan].bg, color: PLAN_COLORS[agent.plan].text }}>
                  {PLAN_LABELS[agent.plan]}
                </span>
              </div>
              <div className="landing__agent-name">{agent.name}</div>
              <div className="landing__agent-desc">{agent.desc}</div>
              <div className="landing__agent-footer">
                <span className="landing__agent-cat">{agent.category}</span>
                <button className="landing__agent-try-btn" onClick={() => handleTryAgent(agent.route)}>
                  Probar →
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="landing__agents-footer">
          <Link to="/auth?mode=register" className="btn btn-primary">
            Conseguir 50 créditos gratis →
          </Link>
        </div>
      </div>
    </section>
  );
}

const HOW_STEPS = [
  { num: '01', defaultTitle: 'Elige el tipo de contenido', defaultDesc: 'Blog, redes sociales, Google Ads, email... Más de 30 plantillas especializadas para tu sector.' },
  { num: '02', defaultTitle: 'Rellena 3 campos sencillos', defaultDesc: 'Tema, público y tono — en lenguaje normal. Sin prompts, sin saber de IA, sin tecnicismos.' },
  { num: '03', defaultTitle: 'Obtén contenido listo para publicar', defaultDesc: 'En 30 segundos tienes el texto, estrategia o imagen generada — estructurada y profesional.' },
  { num: '04', defaultTitle: 'Copia, programa o comparte', defaultDesc: 'Un clic para copiar. Pregunta de seguimiento si quieres ajustar. O genera imágenes en la misma sesión.' },
];

const HIW_AGENTS = [
  { emoji: '✍️', name: 'Artículo de Blog',     active: true },
  { emoji: '📊', name: 'Análisis de Mercado',   active: false },
  { emoji: '🎯', name: 'Estrategia de Campaña', active: false },
  { emoji: '📧', name: 'Secuencia de Email',    active: false },
  { emoji: '💼', name: 'Propuesta de Cliente',  active: false },
];

function HiwMockup({ step, active }) {
  if (step === 0) return (
    <div className="hiw-mock" style={{ width: '100%' }}>
      <div className="hiw-mock__bar-row hiw-mock__chrome-mini">
        <span className="hiw-mock__dot" /><span className="hiw-mock__dot" /><span className="hiw-mock__dot" />
        <div className="hiw-mock__chrome-label">Plantillas IA</div>
      </div>
      <div style={{ padding: '0.5rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {HIW_AGENTS.map((a) => (
          <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', borderRadius: '7px', background: a.active ? 'rgba(99,102,241,0.07)' : 'transparent', border: `1px solid ${a.active ? 'rgba(99,102,241,0.2)' : 'transparent'}` }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0 }}>{a.emoji}</div>
            <span style={{ fontSize: '0.72rem', color: a.active ? 'var(--color-primary-light)' : 'var(--text-muted)', fontWeight: a.active ? 600 : 400 }}>{a.name}</span>
            {a.active && <div style={{ marginLeft: 'auto', width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--color-primary)' }} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
  if (step === 1) {
    const fields = [
      { label: 'Tema', value: 'Beneficios de la ortodoncia invisible' },
      { label: 'Audiencia', value: 'Adultos 30-45 años, España' },
      { label: 'Tono', value: 'Cercano y profesional' },
    ];
    return (
      <div className="hiw-mock" style={{ width: '100%' }}>
        <div className="hiw-mock__bar-row hiw-mock__chrome-mini">
          <span className="hiw-mock__dot" /><span className="hiw-mock__dot" /><span className="hiw-mock__dot" />
          <div className="hiw-mock__chrome-label">✍️ Artículo de Blog</div>
        </div>
        <div style={{ padding: '0.55rem 0.65rem', display: 'flex', flexDirection: 'column', gap: '0.38rem' }}>
          {fields.map((field, i) => (
            <div key={field.label}>
              <div style={{ fontSize: '0.52rem', color: 'var(--text-muted)', marginBottom: '0.12rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{field.label}</div>
              <motion.div
                style={{ height: 24, background: 'var(--bg-surface-2)', borderRadius: 5, display: 'flex', alignItems: 'center', padding: '0 0.5rem', overflow: 'hidden' }}
                animate={{ borderColor: active ? 'rgba(99,102,241,0.45)' : 'rgba(255,255,255,0.07)', boxShadow: active ? '0 0 0 1.5px rgba(99,102,241,0.12)' : '0 0 0 0px transparent' }}
                transition={{ duration: 0.3, delay: active ? i * 0.25 : 0 }}
              >
                <motion.span
                  style={{ fontSize: '0.63rem', whiteSpace: 'nowrap', display: 'block' }}
                  animate={{ opacity: active ? 1 : 0.45, color: active ? 'var(--text-secondary)' : 'var(--text-muted)' }}
                  transition={{ delay: active ? i * 0.25 + 0.1 : 0 }}
                >{field.value}</motion.span>
              </motion.div>
            </div>
          ))}
          <motion.div
            style={{ height: 24, borderRadius: 5, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.63rem', color: '#a5b4fc', fontWeight: 700, marginTop: '0.1rem' }}
            animate={{ opacity: active ? 1 : 0 }}
            transition={{ delay: active ? 0.85 : 0 }}
          >Generar →</motion.div>
        </div>
      </div>
    );
  }
  if (step === 2) {
    const sections = [
      { label: '✦ Título y subtítulos', color: 'rgba(165,180,252,0.8)', lines: [{ w: 90 }, { w: 75 }, { w: 82 }], bg: 'rgba(165,180,252,0.18)', delay: 0.15 },
      { label: '✦ Cuerpo del artículo (600 palabras)', color: 'rgba(52,211,153,0.75)', lines: [{ w: 88 }, { w: 70 }, { w: 60 }], bg: 'rgba(52,211,153,0.13)', delay: 0.65 },
    ];
    return (
      <div className="hiw-mock" style={{ width: '100%' }}>
        <div className="hiw-mock__bar-row hiw-mock__chrome-mini">
          <span className="hiw-mock__dot" /><span className="hiw-mock__dot" /><span className="hiw-mock__dot" />
          <div className="hiw-mock__chrome-label">Gormaran</div>
          <motion.div style={{ marginLeft: 'auto', display: 'flex', gap: 3 }}
            animate={{ opacity: active ? [0, 1, 1, 0] : 0 }}
            transition={{ duration: 1.2, times: [0, 0.1, 0.7, 1], ease: 'easeInOut' }}>
            {[0,1,2].map(i => (
              <motion.div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--color-primary-light)' }}
                animate={active ? { opacity: [0.3,1,0.3], scale: [0.85,1.1,0.85] } : { opacity: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.15, repeat: active ? 1 : 0 }}
              />
            ))}
          </motion.div>
        </div>
        <div style={{ padding: '0.45rem 0.65rem', display: 'flex', flexDirection: 'column', gap: '0.28rem' }}>
          {sections.map((section, si) => (
            <div key={si} style={{ marginBottom: si < sections.length - 1 ? '0.2rem' : 0 }}>
              <motion.div
                style={{ fontSize: '0.57rem', fontWeight: 700, marginBottom: '0.2rem' }}
                animate={{ opacity: active ? 1 : 0.35, color: active ? section.color : 'var(--text-muted)' }}
                transition={{ delay: active ? section.delay : 0, duration: 0.25 }}
              >{section.label}</motion.div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.22rem' }}>
                {section.lines.map((line, li) => (
                  <motion.div key={li}
                    style={{ transformOrigin: 'left', height: 5, borderRadius: 3, background: section.bg }}
                    animate={active
                      ? { scaleX: line.w / 100, opacity: 1 }
                      : { scaleX: (line.w / 100) * 0.65, opacity: 0.35 }}
                    transition={active
                      ? { duration: 0.4, delay: section.delay + 0.12 + li * 0.08, ease: 'easeOut' }
                      : { duration: 0.2 }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (step === 3) return (
    <div className="hiw-mock" style={{ width: '100%' }}>
      <div className="hiw-mock__bar-row hiw-mock__chrome-mini">
        <span className="hiw-mock__dot" /><span className="hiw-mock__dot" /><span className="hiw-mock__dot" />
        <div className="hiw-mock__review-done">✓ Listo para publicar</div>
      </div>
      <div style={{ padding: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {[80, 65, 90, 55, 75].map((w, i) => (
          <div key={i} style={{ height: 6, borderRadius: 3, width: `${w}%`, background: 'rgba(52,211,153,0.18)' }} />
        ))}
        <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem' }}>
          {[{ label: 'Copiar ✓', primary: true }, { label: 'Ajustar' }, { label: 'Imagen' }].map(btn => (
            <div key={btn.label} style={{ flex: 1, textAlign: 'center', padding: '0.28rem 0', borderRadius: 5, fontSize: '0.6rem', background: btn.primary ? 'rgba(99,102,241,0.12)' : 'var(--bg-card,rgba(255,255,255,0.04))', border: `1px solid ${btn.primary ? 'rgba(99,102,241,0.25)' : 'var(--border-color,rgba(255,255,255,0.08))'}`, color: btn.primary ? '#a5b4fc' : 'var(--text-muted)' }}>{btn.label}</div>
          ))}
        </div>
      </div>
    </div>
  );
  return null;
}

function HowItWorksGrid() {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActive((prev) => (prev + 1) % HOW_STEPS.length), 3800);
  }, []);
  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, [startTimer]);
  return (
    <div className="hiw-grid">
      {HOW_STEPS.map((step, i) => (
        <motion.button key={i} className={`hiw-card${active === i ? ' hiw-card--active' : ''}`}
          onClick={() => { setActive(i); startTimer(); }}
          whileHover={{ scale: 1.015 }} transition={{ duration: 0.2 }}
        >
          <div className="hiw-card__visual"><HiwMockup step={i} active={active === i} /></div>
          <div className="hiw-card__footer">
            <span className="hiw-card__num">{step.num}</span>
            <div className="hiw-card__text">
              <div className="hiw-card__title">{step.defaultTitle}</div>
              <div className="hiw-card__desc">{step.defaultDesc}</div>
            </div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}

function JarvisBrain() {
  const outer12 = Array.from({ length: 12 }, (_, i) => {
    const rad = (i * 30 - 90) * Math.PI / 180;
    return { x: +(150 + Math.cos(rad) * 72).toFixed(1), y: +(150 + Math.sin(rad) * 72).toFixed(1) };
  });
  const inner4 = [0, 90, 180, 270].map(deg => {
    const rad = (deg - 90) * Math.PI / 180;
    return { x: +(150 + Math.cos(rad) * 38).toFixed(1), y: +(150 + Math.sin(rad) * 38).toFixed(1) };
  });

  return (
    <div className="jb-wrap" aria-hidden="true">
      <svg viewBox="0 0 300 300" className="jb-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="jb-atm" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#6366f1" stopOpacity="0.28" />
            <stop offset="45%"  stopColor="#7c3aed" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#1e1b4b" stopOpacity="0"    />
          </radialGradient>
          <radialGradient id="jb-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#818cf8" stopOpacity="0.5"  />
            <stop offset="60%"  stopColor="#6366f1" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#4338ca" stopOpacity="0"    />
          </radialGradient>
          <radialGradient id="jb-core" cx="35%" cy="32%" r="68%">
            <stop offset="0%"   stopColor="#e0e7ff" stopOpacity="1"   />
            <stop offset="22%"  stopColor="#c7d2fe" stopOpacity="1"   />
            <stop offset="55%"  stopColor="#818cf8" stopOpacity="1"   />
            <stop offset="85%"  stopColor="#4338ca" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#312e81" stopOpacity="0.8" />
          </radialGradient>
          <filter id="jb-f1" x="-50%"  y="-50%"  width="200%" height="200%">
            <feGaussianBlur stdDeviation="2"  result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="jb-f2" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4"  result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="jb-f3" x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="8"  result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="jb-f4" x="-250%" y="-250%" width="600%" height="600%">
            <feGaussianBlur stdDeviation="18" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="jb-soft">
            <feGaussianBlur stdDeviation="24"/>
          </filter>
          <path id="jb-p-eq1"  d="M 150,42  A 108,108 0 1,1 149.99,42"  fill="none"/>
          <path id="jb-p-eq2"  d="M 150,57  A 93,93   0 1,1 149.99,57"  fill="none"/>
          <path id="jb-p-inn"  d="M 150,90  A 60,60   0 1,1 149.99,90"  fill="none"/>
          <path id="jb-p-tiny" d="M 150,117 A 33,33   0 1,1 149.99,117" fill="none"/>
          <clipPath id="jb-clip"><circle cx="150" cy="150" r="148"/></clipPath>
        </defs>
        <g clipPath="url(#jb-clip)">
          <circle cx="150" cy="150" r="148" fill="url(#jb-atm)"  filter="url(#jb-soft)" className="jb__bg-pulse"/>
          <circle cx="150" cy="150" r="108" fill="url(#jb-halo)" filter="url(#jb-soft)" className="jb__bg-pulse" style={{ animationDelay:'1.1s' }}/>
          <circle cx="150" cy="150" r="130" fill="none" stroke="rgba(99,102,241,0.14)" strokeWidth="0.8" strokeDasharray="10 8" className="jb__ring jb__ring--outer"/>
          <g className="jb__ring jb__ring--tilt1">
            <ellipse cx="150" cy="150" rx="108" ry="29" fill="none" stroke="rgba(139,92,246,0.45)" strokeWidth="1.1"/>
          </g>
          <g className="jb__ring jb__ring--tilt2">
            <ellipse cx="150" cy="150" rx="108" ry="53" fill="none" stroke="rgba(99,102,241,0.3)" strokeWidth="0.9" strokeDasharray="7 11"/>
          </g>
          <circle cx="150" cy="150" r="108" fill="none" stroke="rgba(129,140,248,0.28)" strokeWidth="1" className="jb__ring jb__ring--eq"/>
          <circle cx="150" cy="150" r="108" fill="none" stroke="rgba(34,211,238,0.75)"  strokeWidth="3" className="jb__scan" filter="url(#jb-f1)"/>
          <circle cx="150" cy="150" r="93"  fill="none" stroke="rgba(99,102,241,0.38)"  strokeWidth="1" className="jb__ring jb__ring--mid"/>
          <circle cx="150" cy="150" r="60"  fill="none" stroke="rgba(129,140,248,0.42)" strokeWidth="1.2" strokeDasharray="5 8" className="jb__ring jb__ring--inner"/>
          {outer12.map((n, i) => {
            const n1 = outer12[(i + 1) % 12];
            const n4 = outer12[(i + 4) % 12];
            return (
              <g key={`nc-${i}`}>
                <line x1={n.x} y1={n.y} x2={n1.x} y2={n1.y} stroke="rgba(99,102,241,0.3)"   strokeWidth="0.6"/>
                <line x1={n.x} y1={n.y} x2={n4.x} y2={n4.y} stroke="rgba(129,140,248,0.16)" strokeWidth="0.45"/>
              </g>
            );
          })}
          {outer12.map((n, i) => (
            <line key={`sp-${i}`} x1="150" y1="150" x2={n.x} y2={n.y} stroke="rgba(129,140,248,0.12)" strokeWidth="0.4"/>
          ))}
          {inner4.map((inn, ii) =>
            outer12.slice(ii * 3, ii * 3 + 4).map((out, oi) => (
              <line key={`io-${ii}-${oi}`} x1={inn.x} y1={inn.y} x2={out.x} y2={out.y} stroke="rgba(139,92,246,0.22)" strokeWidth="0.5"/>
            ))
          )}
          {outer12.map((n, i) => (
            <circle key={`on-${i}`} cx={n.x} cy={n.y} r="2.8" fill="#818cf8" filter="url(#jb-f1)" className={`jb__node jb__node--${i}`}/>
          ))}
          {inner4.map((n, i) => (
            <circle key={`in-${i}`} cx={n.x} cy={n.y} r="4.2" fill="#a78bfa" filter="url(#jb-f2)" className={`jb__node jb__node--${i + 12}`}/>
          ))}
          {[0, 0.55, 1.1, 1.65].map((delay, i) => (
            <circle key={`rp-${i}`} cx="150" cy="150" r="16" fill="none" stroke="rgba(129,140,248,0.5)" strokeWidth="1.5" className="jb__ripple" style={{ animationDelay:`${delay}s` }}/>
          ))}
          <circle cx="150" cy="150" r="26" fill="rgba(99,102,241,0.35)"  filter="url(#jb-f4)" className="jb__center-core"/>
          <circle cx="150" cy="150" r="20" fill="none" stroke="rgba(165,180,252,0.6)" strokeWidth="1.2" filter="url(#jb-f1)" className="jb__center-ring"/>
          <circle cx="150" cy="150" r="15" fill="url(#jb-core)" filter="url(#jb-f2)" className="jb__center-core"/>
          <circle cx="150" cy="150" r="7.5" fill="#c7d2fe" filter="url(#jb-f3)" className="jb__center-core"/>
          <circle cx="150" cy="150" r="3.5" fill="#f0f9ff" filter="url(#jb-f1)"/>
          <circle r="5.5" fill="#22d3ee" opacity="0.95" filter="url(#jb-f2)">
            <animateMotion dur="6s" repeatCount="indefinite" calcMode="linear"><mpath href="#jb-p-eq1"/></animateMotion>
          </circle>
          <circle r="4" fill="#c084fc" opacity="0.9" filter="url(#jb-f2)">
            <animateMotion dur="6s" repeatCount="indefinite" begin="-3s" calcMode="linear"><mpath href="#jb-p-eq1"/></animateMotion>
          </circle>
          <circle r="4" fill="#34d399" opacity="0.85" filter="url(#jb-f1)">
            <animateMotion dur="4.5s" repeatCount="indefinite" begin="-0.8s" calcMode="linear"><mpath href="#jb-p-eq2"/></animateMotion>
          </circle>
          <circle r="3.5" fill="#a5b4fc" opacity="0.9" filter="url(#jb-f2)">
            <animateMotion dur="3.5s" repeatCount="indefinite" begin="-0.6s" calcMode="linear"><mpath href="#jb-p-inn"/></animateMotion>
          </circle>
          <circle r="2.5" fill="#f0f9ff" opacity="0.85" filter="url(#jb-f1)">
            <animateMotion dur="3.5s" repeatCount="indefinite" begin="-1.75s" calcMode="linear"><mpath href="#jb-p-inn"/></animateMotion>
          </circle>
          <circle r="2.5" fill="#fbbf24" opacity="0.8" filter="url(#jb-f1)">
            <animateMotion dur="2s" repeatCount="indefinite" calcMode="linear"><mpath href="#jb-p-tiny"/></animateMotion>
          </circle>
        </g>
      </svg>
    </div>
  );
}

const INTEGRATION_LOGOS = [
  { name: 'Gmail',   Icon: () => <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.828l8.073-6.335C21.69 2.28 24 3.434 24 5.457z"/> },
  { name: 'Outlook', Icon: () => <><rect x="1" y="4" width="22" height="16" rx="2" fill="#0078D4"/><circle cx="8.5" cy="12" r="4" fill="white"/><circle cx="8.5" cy="12" r="2.5" fill="#0078D4"/><rect x="14" y="9" width="8" height="1.5" rx="0.75" fill="white"/><rect x="14" y="11.5" width="8" height="1.5" rx="0.75" fill="white"/><rect x="14" y="14" width="8" height="1.5" rx="0.75" fill="white"/></> },
  { name: 'WhatsApp', color: '#25D366', d: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' },
  { name: 'LinkedIn', color: '#0A66C2', d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
  { name: 'Slack',    color: '#4A154B', d: 'M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z' },
  { name: 'n8n', Icon: () => <><circle cx="6" cy="7" r="3" fill="#ea4b71"/><circle cx="18" cy="7" r="3" fill="#ea4b71"/><circle cx="12" cy="18" r="3" fill="#ea4b71"/><path d="M8.5 8.5L10.5 15M15.5 8.5L13.5 15M9 7H15" fill="none" stroke="#ea4b71" strokeWidth="1.5" strokeLinecap="round"/></> },
];

function ProblemMockup() {
  return (
    <div className="ps-mock">
      <div className="ps-mock__header">
        <div className="ps-mock__tabs">
          <span className="ps-mock__tab ps-mock__tab--active">📱 Instagram (sin publicar: 3 sem)</span>
          <span className="ps-mock__tab">📧 Newsletter pendiente</span>
          <span className="ps-mock__tab">✍️ Blog parado</span>
        </div>
      </div>
      <div className="ps-mock__body">
        {[
          { from: 'Instagram', subject: 'Sin publicar desde hace 3 semanas', time: '—', urgent: true },
          { from: 'Newsletter', subject: 'Pendiente desde mayo — sin enviar', time: '—', urgent: false },
          { from: 'Blog', subject: 'Último artículo: hace 4 meses', time: '—', urgent: true },
          { from: 'Google Ads', subject: 'Anuncios por configurar — sin lanzar', time: '—', urgent: false, muted: true },
        ].map((m, i) => (
          <div key={i} className={`ps-mock__row ${m.urgent ? 'ps-mock__row--urgent' : ''} ${m.muted ? 'ps-mock__row--muted' : ''}`}>
            <div className="ps-mock__avatar">{m.from[0]}</div>
            <div className="ps-mock__info">
              <span className="ps-mock__from">{m.from} {m.urgent && <span className="ps-mock__tag ps-mock__tag--red">🔴 Pendiente</span>}</span>
              <span className="ps-mock__subject">{m.subject}</span>
            </div>
            <span className="ps-mock__time">{m.time}</span>
          </div>
        ))}
        <div className="ps-mock__badges">
          <span className="ps-mock__badge ps-mock__badge--red">⏱ 3-4 horas por post</span>
          <span className="ps-mock__badge ps-mock__badge--red">😓 Sin equipo de marketing</span>
          <span className="ps-mock__badge ps-mock__badge--red">🤷 No sé qué pedirle a ChatGPT</span>
        </div>
      </div>
    </div>
  );
}

function SolutionMockup() {
  return (
    <div className="ps-mock ps-mock--solution">
      <div className="ps-mock__header">
        <div className="ps-mock__tabs">
          <span className="ps-mock__tab ps-mock__tab--active ps-mock__tab--green">📥 Contenido del mes (4 piezas)</span>
          <span className="ps-mock__tab">🧠 Cerebro de Marca</span>
          <span className="ps-mock__tab">📋 Historial</span>
        </div>
        <span className="ps-mock__ai-on">🤖 IA activa</span>
      </div>
      <div className="ps-mock__body">
        {[
          { from: 'Instagram',  subject: 'Post semanal',         ch: '📱', priority: 'Alta',  status: '✅ Generado en 30 seg', sc: '#34d399' },
          { from: 'Newsletter', subject: 'Junio 2026',           ch: '📧', priority: 'Alta',  status: '✅ Listo para enviar',  sc: '#34d399' },
          { from: 'Blog',       subject: 'Artículo SEO',         ch: '✍️', priority: 'Media', status: '🤖 En proceso',         sc: '#a5b4fc' },
          { from: 'Google Ads', subject: '5 anuncios listos',    ch: '🎯', priority: 'Baja',  status: '📋 En borrador',        sc: '#fbbf24' },
        ].map((m, i) => (
          <div key={i} className="ps-mock__row ps-mock__row--task">
            <span className="ps-mock__ch-icon">{m.ch}</span>
            <div className="ps-mock__info">
              <span className="ps-mock__from">{m.from}</span>
              <span className="ps-mock__subject">{m.subject}</span>
            </div>
            <div className="ps-mock__task-meta">
              <span className="ps-mock__priority" style={{ color: m.priority === 'Alta' ? '#f87171' : m.priority === 'Media' ? '#fbbf24' : '#6b7280' }}>{m.priority}</span>
              <span className="ps-mock__status" style={{ color: m.sc }}>{m.status}</span>
            </div>
          </div>
        ))}
        <div className="ps-mock__badges">
          <span className="ps-mock__badge ps-mock__badge--green">⚡ 30 segundos por pieza</span>
          <span className="ps-mock__badge ps-mock__badge--green">✅ Sin escribir un solo prompt</span>
          <span className="ps-mock__badge ps-mock__badge--green">🧠 Siempre en tu tono de marca</span>
        </div>
      </div>
    </div>
  );
}

function ProblemSolutionSection() {
  const [isSolution, setIsSolution] = useState(false);
  return (
    <section className="landing__ps section">
      <div className="container">
        <AnimatedSection>
          <span className={`section-pill landing__ps-pill ${isSolution ? 'landing__ps-pill--ok' : 'landing__ps-pill--err'}`}>
            {isSolution ? '✓ LA SOLUCIÓN' : '⊘ EL PROBLEMA'}
          </span>
          <h2 className="section-title">
            {isSolution
              ? <>Con Gormaran, contenido listo en <span className="gradient-text">30 segundos</span></>
              : <>Sabes que deberías publicar más. <span style={{ color: '#f87171' }}>Pero nunca hay tiempo.</span></>}
          </h2>
          <p className="section-subtitle">
            {isSolution
              ? 'Describes tu negocio una vez. Eliges la plantilla. Rellenas 3 campos. La IA genera el contenido siempre en tu tono — sin que escribas un solo prompt.'
              : 'Sin equipo de marketing ni agencia, cada post cuesta horas. ChatGPT da respuestas genéricas si no sabes qué pedirle. El resultado: meses sin publicar.'}
          </p>
        </AnimatedSection>

        <div className="landing__ps-toggle-wrap">
          <span className={`landing__ps-label ${!isSolution ? 'landing__ps-label--active' : ''}`}>Sin Gormaran</span>
          <button
            className={`landing__ps-toggle ${isSolution ? 'landing__ps-toggle--on' : ''}`}
            onClick={() => setIsSolution(v => !v)}
            aria-label="Cambiar entre problema y solución"
          >
            <span className="landing__ps-thumb" />
          </button>
          <span className={`landing__ps-label ${isSolution ? 'landing__ps-label--active' : ''}`}>Con Gormaran</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={isSolution ? 'sol' : 'prob'}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.22 }}
          >
            {isSolution ? <SolutionMockup /> : <ProblemMockup />}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  { name: 'Marta A.', role: 'Directora de agencia de marketing, Madrid', quote: 'Gestiono 12 marcas y Aran genera el contenido de cada una en su tono exacto. Lo que antes eran 3 días de trabajo ahora es una mañana.', avatar: 'M' },
  { name: 'Carlos V.', role: 'Consultor de marketing independiente, Barcelona', quote: 'Tenía instrucciones de marca de cada cliente en archivos de Word. Con el Cerebro de Marca, Aran ya lo sabe todo desde el primer mensaje — sin que yo repita nada.', avatar: 'C' },
  { name: 'Laura M.', role: 'CEO de consultora de comunicación, Valencia', quote: 'Onboardeamos una nueva marca en minutos con el asistente de configuración. El contenido sale alineado con su sector y tono desde el primer intento.', avatar: 'L' },
  { name: 'Javier R.', role: 'Consultor de marketing, Barcelona', quote: 'Gestiono 7 clientes y con Gormaran produzco el triple sin contratar a nadie. El Cerebro de Marca por cliente es un cambio de juego total.', avatar: 'J' },
];

function TestimonialsSection() {
  return (
    <section className="landing__testimonials section">
      <div className="container">
        <AnimatedSection>
          <span className="section-pill">Resultados reales</span>
          <h2 className="section-title">
            Lo que dicen{' '}
            <span className="gradient-text">nuestros clientes</span>
          </h2>
        </AnimatedSection>
        <motion.div
          className="landing__testimonials-grid"
          initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: '-20px' }}
          variants={stagger}
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={i} variants={fadeUp} className="landing__testimonial-card">
              <p className="landing__testimonial-quote">"{t.quote}"</p>
              <div className="landing__testimonial-author">
                <div className="landing__testimonial-avatar">{t.avatar}</div>
                <div>
                  <div className="landing__testimonial-name">{t.name}</div>
                  <div className="landing__testimonial-role">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  { q: '¿Necesito saber de IA o escribir prompts?', a: 'No. Ese es exactamente el punto. Los prompts están integrados en cada plantilla. Tú solo rellenas campos en lenguaje normal — tema, audiencia, tono — y la IA hace el resto. Sin iteraciones, sin aprender a "hablar con la IA".' },
  { q: '¿Qué es Gormaran exactamente?', a: 'Gormaran.io es una plataforma de inteligencia artificial diseñada para ayudar a agencias de marketing y consultoras de comunicación a gestionar a sus clientes desde un solo lugar. En lugar de invertir horas en tareas repetitivas — planificar contenidos, revisar señales, coordinar acciones y hacer seguimiento — la plataforma genera propuestas de contenido, reúne información de distintas fuentes (emails, redes sociales, calendario) y las convierte en acciones concretas. Aran, el asistente de IA integrado con WhatsApp, permite revisar tareas, aprobar propuestas y activar automatizaciones directamente desde el móvil. Desde 15€/mes.' },
  { q: '¿Puedo usar Gormaran desde WhatsApp?', a: 'Sí. Aran, el asistente de IA de Gormaran.io, está integrado y sincronizado con WhatsApp. Conectas tu número una vez y desde ahí gestionas todas tus marcas: revisas tareas pendientes, apruebas propuestas de contenido, activas automatizaciones — sin abrir el panel. Si tienes varias marcas, Aran detecta de cuál se trata o te pregunta antes de actuar.' },
  { q: '¿En qué se diferencia de ChatGPT?', a: 'ChatGPT no recuerda quiénes son tus clientes. Cada vez empiezas de cero. Gormaran.io aprende el Cerebro de Marca de cada cliente — tono, audiencia, sector, propuesta de valor — y genera contenido en su voz desde el primer intento. Sin prompts, sin iteraciones, sin mezclar voces entre marcas.' },
  { q: '¿Para qué tipo de negocios sirve?', a: 'Está pensada para agencias de marketing, consultoras de comunicación y profesionales que gestionan más de una marca al mismo tiempo. Con Gormaran.io pueden automatizar hasta el 70% de las tareas de gestión de contenido, lo que les permite dedicar su tiempo a lo que de verdad importa: la estrategia y los resultados de sus clientes. El onboarding por sector (restaurante, clínica, inmobiliaria, e-commerce…) permite configurar una nueva marca en minutos.' },
  { q: '¿Cómo funcionan los créditos?', a: 'Cada plan incluye un pool de créditos mensuales — Grow 500/mes, Scale 2.000/mes, Evolution ilimitados. Cada acción consume: texto/chat 2 créditos, imagen 10, vídeo 25, audio 10, herramientas avanzadas 30. Empiezas con 50 créditos gratis sin tarjeta.' },
  { q: '¿Puedo probarlo antes de pagar?', a: 'Sí — 50 créditos gratis, sin tarjeta de crédito. Suficiente para generar 25 textos o explorar todas las plantillas. Si te convence, pasas a un plan de pago. Si no, no hay cargo.' },
  { q: '¿Mis datos son privados?', a: 'Sí. Tu información de negocio, tus inputs y tus outputs se almacenan de forma privada en tu workspace. No se comparten con otros usuarios ni se usan para entrenar modelos de IA.' },
  { q: '¿Puedo cancelar cuando quiera?', a: 'Sí — sin permanencia, sin penalizaciones. Tu acceso continúa hasta el final del período de facturación.' },
];

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="landing__faq section">
      <div className="container container-sm">
        <AnimatedSection>
          <span className="section-pill">FAQ</span>
          <h2 className="section-title">
            Preguntas <span className="gradient-text">frecuentes</span>
          </h2>
        </AnimatedSection>
        <div className="landing__faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className={`landing__faq-item${openIdx === i ? ' landing__faq-item--open' : ''}`}>
              <button className="landing__faq-question" onClick={() => setOpenIdx(openIdx === i ? null : i)}>
                {item.q}
                <span className="landing__faq-chevron">{openIdx === i ? '−' : '+'}</span>
              </button>
              <AnimatePresence>
                {openIdx === i && (
                  <motion.div className="landing__faq-answer" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <p>{item.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const CLIENT_FEATURES = [
  {
    icon: '🧠',
    title: 'Cerebro de Marca',
    desc: 'Describes tu negocio una vez — sector, tono, audiencia, valores. La IA lo recuerda en cada generación. El resultado siempre suena como tú.',
  },
  {
    icon: '🏷',
    title: 'Cartera de marcas',
    desc: 'Gestiona varias marcas desde un solo dashboard. Cada marca tiene su propio cerebro, pipeline y buzón — sin mezclar clientes.',
  },
  {
    icon: '📥',
    title: 'Buzón de señales',
    desc: 'Emails, formularios y señales de cliente — todo llega al Buzón. Aran propone la respuesta o pieza de contenido al lado de cada señal.',
  },
  {
    icon: '✨',
    title: 'Contenido siempre en marca',
    desc: 'Abre Gormaran, elige la plantilla y pulsa Generar — la IA ya conoce tu negocio y el tono sale perfecto desde el primer intento.',
  },
  {
    icon: '💬',
    title: 'Aran en WhatsApp',
    desc: 'Aran está integrado y sincronizado con WhatsApp. Desde el móvil puedes gestionar todas tus marcas, revisar tareas pendientes, aprobar propuestas de contenido y activar automatizaciones — sin abrir ningún panel.',
  },
];

const LIVE_FEATURES = [
  { icon: '🧠', label: 'Cerebro de cuenta',         detail: 'Voz única en todas las generaciones' },
  { icon: '🏷', label: 'Cartera de marcas',          detail: 'Un workspace por cliente, sin mezclas' },
  { icon: '📥', label: 'Buzón de señales',           detail: 'Señales del cliente → propuesta IA al lado' },
  { icon: '✅', label: 'Pipeline de aprobación',     detail: 'Pendiente → Programado → Publicado' },
  { icon: '✦',  label: 'Aran, tu IA de marketing',  detail: 'Asistente contextual siempre disponible' },
  { icon: '💬', label: 'Aran en WhatsApp',           detail: 'Gestiona todas tus marcas por chat — sin abrir el panel' },
  { icon: '✨', label: 'Generador de contenido',     detail: 'Posts, emails, artículos — 30 segundos' },
  { icon: '🔄', label: 'Actividad de la IA',         detail: 'Log de lo que generó mientras no estabas' },
];

function BrainHierarchySection() {
  return (
    <section className="lp-hierarchy section">
      <div className="container">
        <div className="lp-hierarchy__head">
          <span className="section-pill">Arquitectura de marca</span>
          <h2 className="section-title">
            Tu cerebro de cuenta.<br />
            <span className="gradient-text">Un sub-cerebro por cada marca.</span>
          </h2>
          <p className="section-subtitle">
            Gormaran aprende la identidad de tu agencia una vez. Cada marca cliente tiene su propio cerebro: su tono, su audiencia, sus valores. La IA nunca mezcla voces.
          </p>
        </div>
        <div className="lp-hierarchy__diagram">
          <div className="lp-hierarchy__root">
            <span className="lp-hierarchy__root-icon">🧠</span>
            <span className="lp-hierarchy__root-label">Tu Cerebro de Cuenta</span>
            <span className="lp-hierarchy__root-sub">Voz de tu agencia · Metodología · Identidad</span>
          </div>
          <div className="lp-hierarchy__line" aria-hidden="true" />
          <div className="lp-hierarchy__brands">
            {['Marca A', 'Marca B', 'Marca C'].map((name, i) => (
              <div key={i} className="lp-hierarchy__brand">
                <span className="lp-hierarchy__brand-icon">🏷</span>
                <span className="lp-hierarchy__brand-name">{name}</span>
                <span className="lp-hierarchy__brand-chip">Cerebro de Marca</span>
              </div>
            ))}
            <div className="lp-hierarchy__brand lp-hierarchy__brand--more">
              <span className="lp-hierarchy__brand-icon">+</span>
              <span className="lp-hierarchy__brand-name">Más marcas</span>
              <span className="lp-hierarchy__brand-chip">Sin límite</span>
            </div>
          </div>
        </div>
        <p className="lp-hierarchy__footer">
          Aran, tu asistente de IA, conoce todos los cerebros y genera contenido siempre en la voz correcta — sin que tengas que repetir el contexto cada vez.
        </p>
      </div>
    </section>
  );
}

const WA_CONVO = [
  { from: 'user', text: 'Genera un post de Instagram para Agromotor sobre la nueva temporada de cosecha' },
  { from: 'aran', text: '🌾 Cosecha en marcha en Grupo Agromotor\n\nTemporada cargada, equipos a pleno rendimiento. Cada pieza de maquinaria lista para que el campo dé lo mejor de sí.\n\nCalidad. Precisión. Compromiso.\n\n#Agromotor #Cosecha #Maquinariaagricola #Agro' },
  { from: 'user', text: 'Ahora uno para RojaFarm — ferias de otoño' },
  { from: 'aran', text: '🍂 RojaFarm en las ferias de otoño\n\nNos vemos en la temporada de ferias. Ven a conocer nuestra gama y habla con nuestro equipo en persona.\n\n📍 Fechas en nuestra web\n\n#RojaFarm #FeriasAgricolas #Otoño2025' },
];

function WhatsAppSection() {
  return (
    <section className="lp-wa section">
      <div className="container">
        <div className="lp-wa__grid">
          <motion.div
            className="lp-wa__text"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section-pill">💬 WhatsApp Business</span>
            <h2 className="section-title">
              Todas tus marcas.<br />
              <span className="gradient-text">Desde un solo chat.</span>
            </h2>
            <p className="section-subtitle" style={{ textAlign: 'left' }}>
              Aran está integrado y sincronizado con WhatsApp. Conectas tu número una vez y desde ahí gestionas toda tu cartera de marcas — sin abrir ningún panel.
            </p>
            <ul className="lp-wa__bullets">
              <li><span className="lp-wa__bullet-icon">✦</span>Cambia de marca con un mensaje — Aran detecta el contexto solo</li>
              <li><span className="lp-wa__bullet-icon">✦</span>Genera posts listos para copiar en segundos</li>
              <li><span className="lp-wa__bullet-icon">✦</span>Aprueba o modifica contenido del pipeline desde el móvil</li>
              <li><span className="lp-wa__bullet-icon">✦</span>Crea y gestiona automatizaciones por chat</li>
            </ul>
          </motion.div>

          <motion.div
            className="lp-wa__mock"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="lp-wa__phone">
              <div className="lp-wa__phone-header">
                <div className="lp-wa__phone-avatar">A</div>
                <div className="lp-wa__phone-meta">
                  <div className="lp-wa__phone-name">Aran · Gormaran</div>
                  <div className="lp-wa__phone-status">en línea</div>
                </div>
              </div>
              <div className="lp-wa__phone-body">
                {WA_CONVO.map((m, i) => (
                  <div key={i} className={`lp-wa__bubble lp-wa__bubble--${m.from}`}>
                    {m.text.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < m.text.split('\n').length - 1 && <br />}</span>
                    ))}
                  </div>
                ))}
              </div>
              <div className="lp-wa__phone-input">
                <span className="lp-wa__phone-input-text">Escribe un mensaje…</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FeaturesLiveSection() {
  return (
    <section className="lp-live section">
      <div className="container">
        <AnimatedSection>
          <span className="section-pill lp-live__pill">En producción hoy</span>
          <h2 className="section-title">
            Lo que prometen.<br />
            <span className="gradient-text">Lo que está funcionando.</span>
          </h2>
          <p className="section-subtitle">
            Sin roadmaps de humo. Cada función de abajo la puedes usar ahora mismo.
          </p>
        </AnimatedSection>

        <motion.div
          className="lp-live__grid"
          initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: '-20px' }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
        >
          {LIVE_FEATURES.map(f => (
            <motion.div
              key={f.label}
              className="lp-live__card"
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } }}
            >
              <div className="lp-live__card-top">
                <span className="lp-live__icon">{f.icon}</span>
                <span className="lp-live__badge">✓ Activo</span>
              </div>
              <p className="lp-live__label">{f.label}</p>
              <p className="lp-live__detail">{f.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ClientWorkspaceSection() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  return (
    <section className="landing__cws section">
      <div className="container">
        <AnimatedSection>
          <span className="section-pill">Tu espacio de trabajo</span>
          <h2 className="section-title">
            Todo lo que necesitas{' '}
            <span className="gradient-text">en un solo lugar</span>
          </h2>
          <p className="section-subtitle">
            Tu Cerebro de Marca, tu historial de contenido y tus tareas pendientes — siempre a la vista, siempre actualizadas.
          </p>
        </AnimatedSection>

        <motion.div
          className="landing__cws-grid"
          initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: '-20px' }}
          variants={stagger}
        >
          {CLIENT_FEATURES.map((f) => (
            <motion.div key={f.title} variants={fadeUp} className="landing__cws-card">
              <div className="landing__cws-icon">{f.icon}</div>
              <h3 className="landing__cws-title">{f.title}</h3>
              <p className="landing__cws-desc">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="landing__cws-mockup"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <div className="landing__cws-mockup-chrome">
            <span className="landing__hero-sc-dot" />
            <span className="landing__hero-sc-dot" />
            <span className="landing__hero-sc-dot" />
            <span className="landing__hero-sc-bar">gormaran.io/workspace</span>
          </div>
          <div className="landing__cws-mockup-body">
            <div className="landing__cws-kanban">
              {[
                { label: 'Pendiente', color: '#6366f1', cards: ['Post Instagram julio', 'Newsletter mensual'] },
                { label: 'En proceso', color: '#f59e0b', cards: ['Artículo blog SEO'] },
                { label: 'Publicado', color: '#10b981', cards: ['Google Ads verano', 'Copy web principal', 'Post LinkedIn'] },
              ].map(col => (
                <div key={col.label} className="landing__cws-col">
                  <div className="landing__cws-col-header" style={{ color: col.color }}>
                    <span className="landing__cws-col-dot" style={{ background: col.color }} />
                    {col.label}
                    <span className="landing__cws-col-count">{col.cards.length}</span>
                  </div>
                  {col.cards.map(card => (
                    <div key={card} className="landing__cws-card-mini">{card}</div>
                  ))}
                </div>
              ))}
            </div>
            <div className="landing__cws-workflow">
              <div className="landing__cws-workflow-label">⚡ Automatización activa</div>
              <div className="landing__cws-workflow-flow">
                <div className="landing__cws-wf-node landing__cws-wf-node--trigger">
                  <span>📅</span> Lunes 9h — generar post
                </div>
                <div className="landing__cws-wf-arrow">→</div>
                <div className="landing__cws-wf-node landing__cws-wf-node--action">
                  <span>📱</span> Post Instagram listo
                </div>
                <div className="landing__cws-wf-arrow">→</div>
                <div className="landing__cws-wf-node landing__cws-wf-node--action">
                  <span>✅</span> Enviado a revisión
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="landing__agents-footer" style={{ marginTop: '2rem' }}>
          <button
            className="btn btn-primary"
            onClick={() => currentUser ? navigate('/dashboard') : navigate('/auth?mode=register')}
          >
            {currentUser ? 'Ir al Dashboard →' : 'Crear tu espacio de trabajo gratis →'}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── AvatarReelsSection — "Graba sin grabarte" ─────────────────────────────── */
/* Shown only when REACT_APP_FEATURE_AVATAR_REELS=true                          */
function AvatarReelsSection() {
  const steps = [
    { icon: '📸', title: 'Sube 5 fotos', desc: 'Cara frontal, perfil, plano medio. El sistema entrena tu avatar en 2-4 minutos.' },
    { icon: '🔗', title: 'Pega una URL', desc: 'Artículo, post de blog, página de producto — la IA lee y escribe el guión.' },
    { icon: '✏️', title: 'Revisa el guión', desc: 'Ajústalo con una instrucción en texto plano. El vídeo no se genera hasta que lo apruebes.' },
    { icon: '🎬', title: 'Tu vídeo listo', desc: 'Avatar con tu cara y voz sintética, listo para publicar en Instagram, LinkedIn o TikTok.' },
  ];

  return (
    <section className="landing__agents section" id="graba-sin-grabarte" style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
      <div className="container">
        <AnimatedSection>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-pill">Nuevo</span>
            <h2 className="section-title" style={{ marginTop: '0.75rem' }}>
              Graba sin grabarte
            </h2>
            <p className="section-subtitle" style={{ maxWidth: 520, margin: '1rem auto 0' }}>
              Tu avatar IA habla por ti. Convierte cualquier URL en un reel de 30 segundos
              —&nbsp;sin cámara, sin guión escrito a mano, sin edición.
            </p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              ⚠️ Requiere consentimiento explícito · Contenido etiquetado como IA (AI Act UE)
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', maxWidth: 860, margin: '0 auto 2.5rem' }}>
            {steps.map((s, i) => (
              <div key={i} style={{
                background: 'rgba(99,102,241,0.06)',
                border: '1px solid rgba(99,102,241,0.18)',
                borderRadius: 12,
                padding: '1.25rem',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{s.icon}</div>
                <div style={{ fontWeight: 700, marginBottom: '0.35rem', fontSize: '0.9rem' }}>{s.title}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/auth?mode=register" className="btn btn-primary">
              Crear mi avatar →
            </Link>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.75rem' }}>
              120 créditos por reel · ~€1.20 · Avatar incluido en tu cuenta
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { currentUser } = useAuth();
  const [userCount, setUserCount] = useState(427);
  const [showDemo, setShowDemo] = useState(false);

  const seoEl = useSEO({
    title: 'Gormaran.io — IA para Agencias de Marketing y Consultoras de Comunicación',
    description: 'Gormaran.io ayuda a agencias de marketing y consultoras de comunicación a gestionar todos sus clientes desde un solo lugar. Genera contenido en el tono de cada marca, automatiza el 70% del trabajo repetitivo y gestiona todo desde WhatsApp. Desde 15€/mes.',
    canonical: 'https://gormaran.io/',
  });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub-2.onrender.com'}/health`).catch(() => {});
    fetch(`${process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub-2.onrender.com'}/api/stats`)
      .then(r => r.json())
      .then(d => { if (d.userCount > 0) setUserCount(d.userCount); })
      .catch(() => {});
  }, []);

  return (
    <>{seoEl}<div className="landing">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="landing__hero">
        <div className="landing__hero-bg" aria-hidden="true">
          <div className="landing__orb landing__orb-1" />
          <div className="landing__orb landing__orb-2" />
          <div className="landing__orb landing__orb-3" />
          <div className="landing__jarvis-orb" />
          <div className="landing__grid-pattern" />
        </div>

        <div className="container">
          {/* Headline */}
          <motion.div className="landing__hero-content" initial="hidden" animate="visible" variants={stagger}>
            <motion.div className="landing__ap-wrap" variants={fadeUp} transition={{ duration: 0.3 }}>
              <span className="landing__ap-dept">Tu departamento de marketing desde 15€/mes</span>
            </motion.div>
            <motion.h1 className="landing__hero-title" variants={fadeUp} transition={{ duration: 0.35, delay: 0.05 }}>
              Gestiona todos tus clientes
              <br />
              <span className="gradient-text">desde un solo lugar.</span>
            </motion.h1>
          </motion.div>

          {/* Demo panel */}
          <motion.div
            className="landing__hero-demo"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
          >
            <AranDemoPanel />
          </motion.div>

          {/* CTAs + trust + integrations below demo */}
          <motion.div className="landing__hero-below" initial="hidden" animate="visible" variants={stagger}>
            <motion.div className="landing__hero-actions" variants={fadeUp} transition={{ duration: 0.35, delay: 0 }}>
              <Link to={currentUser ? '/dashboard' : '/auth?mode=register'} className="btn btn-primary btn-lg">
                {currentUser ? 'Ir al Dashboard →' : 'Empieza gratis →'}
              </Link>
              <button className="btn btn-secondary btn-lg" onClick={() => setShowDemo(true)}>
                Ver cómo funciona ▶
              </button>
            </motion.div>

            <motion.div className="landing__hero-trust" variants={fadeUp} transition={{ duration: 0.35, delay: 0.05 }}>
              <span className="landing__trust-item"><span className="landing__trust-check">✓</span>Nueva marca lista en minutos</span>
              <span className="landing__trust-divider">·</span>
              <span className="landing__trust-item"><span className="landing__trust-check">✓</span>Automatiza el 70% del trabajo repetitivo</span>
              <span className="landing__trust-divider">·</span>
              <span className="landing__trust-item"><span className="landing__trust-check">✓</span>Gestiona todo desde WhatsApp</span>
            </motion.div>

            <motion.div className="landing__integrations" variants={fadeUp} transition={{ duration: 0.35, delay: 0.1 }}>
              <span className="landing__int-label">SE INTEGRA CON</span>
              <div className="landing__int-logos">
                {INTEGRATION_LOGOS.map(logo => (
                  <div key={logo.name} className="landing__int-logo" title={logo.name}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      {logo.Icon ? <logo.Icon /> : <path d={logo.d} fill={logo.color} />}
                    </svg>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── VALUE PROP BAR ─────────────────────────────────── */}
      <div className="landing__value-bar">
        <div className="container">
          <span className="landing__value-pill">✦ Aran, tu IA de marketing</span>
          <span className="landing__value-text">Conoce la voz exacta de cada marca · Posts, emails, anuncios y estrategia · Sin prompts, sin repetir contexto</span>
          <Link to="/pricing" className="landing__value-cta">Ver planes →</Link>
        </div>
      </div>

      {/* ── TRUST BAR ──────────────────────────────────────── */}

      {/* ── PROBLEMA / SOLUCIÓN ─────────────────────────────── */}
      <ProblemSolutionSection />

      {/* ── CEREBRO HIERARCHY ──────────────────────────────── */}
      <BrainHierarchySection />

      {/* ── CLIENT WORKSPACE ───────────────────────────────── */}
      <ClientWorkspaceSection />

      {/* ── TESTIMONIALS ───────────────────────────────────── */}
      <TestimonialsSection />

      {/* ── PRICING ────────────────────────────────────────── */}
      <section className="landing__plans section">
        <div className="container">
          <AnimatedSection>
            <span className="section-pill">Precios</span>
            <h2 className="section-title">
              Para agencias y consultoras. <span className="gradient-text">Desde 15€/mes.</span>
            </h2>
          </AnimatedSection>
          <motion.div className="landing__plans2" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>

            <motion.div className="landing__plan2" variants={fadeUp}>
              <h3 className="landing__plan2-name">Free</h3>
              <div className="landing__plan2-price">€0 <span>/mes</span></div>
              <ul className="landing__plan2-features">
                <li>✓ 50 generaciones gratis</li>
                <li>✓ Plantillas de Marketing y Contenido</li>
                <li>✓ 1 marca · Cerebro de Marca básico</li>
                <li className="landing__plan2-locked">✗ Multi-marca / cartera de clientes</li>
              </ul>
              <Link to="/auth?mode=register" className="btn btn-secondary landing__plan2-cta">
                Empezar gratis
              </Link>
            </motion.div>

            <motion.div className="landing__plan2 landing__plan2--pro" variants={fadeUp}>
              <div className="landing__plan2-badge">⭐ Más popular</div>
              <h3 className="landing__plan2-name">Grow</h3>
              <div className="landing__plan2-price">€15 <span>/mes (anual)</span></div>
              <p className="landing__plan2-annual">o €19/mes mensual</p>
              <ul className="landing__plan2-features">
                <li>✓ <strong>500 créditos/mes incluidos</strong></li>
                <li>✓ Hasta <strong>3 marcas cliente</strong> · Cerebro de Marca por cada una</li>
                <li style={{ color: '#25d366', fontWeight: 600 }}>✓ 💬 Aran en WhatsApp — gestiona marcas por chat</li>
                <li>✓ Plantillas de Marketing, Contenido y Digital</li>
              </ul>
              <Link to="/pricing" className="btn btn-primary landing__plan2-cta">
                Ver plan Grow →
              </Link>
              <p className="landing__plan2-guarantee">
                🔒 7 días de garantía · Sin permanencia
              </p>
            </motion.div>

            <motion.div className="landing__plan2" variants={fadeUp}>
              <h3 className="landing__plan2-name">Scale</h3>
              <div className="landing__plan2-price">€39 <span>/mes (anual)</span></div>
              <p className="landing__plan2-annual">o €49/mes mensual</p>
              <ul className="landing__plan2-features">
                <li>✓ <strong>2.000 créditos/mes incluidos</strong></li>
                <li>✓ <strong>✦ Supercomputer</strong> — equipo de IA completo en una línea</li>
                <li>✓ Hasta <strong>5 marcas cliente</strong> · Cerebro de Marca por cada una</li>
                <li>✓ Herramientas de Agencia (propuesta, informe, caso de éxito)</li>
                <li>✓ Estudio Creativo · E-commerce · Estrategia</li>
              </ul>
              <Link to="/pricing" className="btn btn-secondary landing__plan2-cta">
                Ver Scale →
              </Link>
            </motion.div>
          </motion.div>

          <div className="landing__plans-footer">
            <Link to="/pricing" className="btn btn-ghost btn-sm">
              Ver todos los planes (Evolution, Add-ons) →
            </Link>
          </div>
        </div>
      </section>

      {/* ── GRABA SIN GRABARTE (feature-flagged) ──────────── */}
      {process.env.REACT_APP_FEATURE_AVATAR_REELS === 'true' && <AvatarReelsSection />}

      {/* ── FAQ ───────────────────────────────────────────── */}
      <FAQSection />

      {/* ── FINAL CTA ─────────────────────────────────────── */}
      <section className="landing__cta-full section">
        <div className="container">
          <AnimatedSection>
            <div className="landing__cta-full-card">
              <span className="landing__hero-badge landing__cta-full-badge">
                Empieza gratis hoy
              </span>
              <h2 className="landing__cta-full-title">
                Tu negocio, publicando
                <br />
                <span className="gradient-text">
                  como una gran marca.
                </span>
              </h2>
              <p className="landing__cta-full-subtitle">
                Más de 30 plantillas especializadas. Contenido listo en 30 segundos. Sin escribir un solo prompt.
              </p>
              <div className="landing__cta-actions">
                <Link to={currentUser ? '/dashboard' : '/auth?mode=register'} className="btn btn-primary btn-lg">
                  {currentUser ? 'Ir al Dashboard →' : 'Empieza gratis →'}
                </Link>
                <Link to="/pricing" className="btn btn-secondary btn-lg">
                  Ver precios →
                </Link>
              </div>
              <div className="landing__cta-trust">
                <span className="landing__trust-item"><span className="landing__trust-check">✓</span>Sin tarjeta de crédito</span>
                <span className="landing__trust-divider">·</span>
                <span className="landing__trust-item"><span className="landing__trust-check">✓</span>50 créditos gratis</span>
                <span className="landing__trust-divider">·</span>
                <span className="landing__trust-item"><span className="landing__trust-check">✓</span>Cancela cuando quieras</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <DemoVideoModal isOpen={showDemo} onClose={() => setShowDemo(false)} />
    </div></>
  );
}
